import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { isAfter, isEqual } from "date-fns";

import { formatDateToISO, isCalendarDayPast } from "../../../../utils/Helper";
import { scrollToTop } from "../../../../utils/scrollToTop";
import { useGetIndividualQuery } from "../../../../store/Company/companyApiSlice";
import { profileStatus, LISTING, WARNING } from "./profileStatus";

/* The "Get The Business" flow. */

/* Transcribed from [id].jsx: an enterprise counts if it has credits left, or
   is a partner program regardless of credits. */
export const eligibleEnterprises = (userInfo) =>
  (userInfo?.enterprise_solutions || []).filter(
    (e) =>
      (e?.credits !== undefined && e?.credits !== null && e?.credits > 0) ||
      e?.enterprise_type === "PARTNER_PROGRAM"
  );

/* Transcribed from [id].jsx: an active, non-pay-as-you-go subscription whose
   end date has not passed — compared inclusively. */
export const activeSubscription = (individualData, today = new Date()) =>
  individualData?.individualSubscription?.find(
    (sub) =>
      sub?.status === "active" &&
      sub?.subscription_type !== "pay-as-you-go" &&
      (isAfter(new Date(sub.date_of_end), today) ||
        isEqual(new Date(sub.date_of_end), today))
  );

/* The viewer can spend a credit only when their OWN subscription is genuinely usable. */
export const canSpendCredit = ({ subscriptionActive }) => {
  if (!subscriptionActive) return false;
  if (String(subscriptionActive.status ?? "").toLowerCase() !== "active") return false;
  if (isCalendarDayPast(subscriptionActive.date_of_end)) return false;
  return subscriptionActive.no_of_profile_views >= 1;
};

/* Transcribed verbatim from createWorldBoxOrder. */
export const customerReference = (formData) => {
  if (!formData?.name?.trim()) return "No reference data provided";
  return `Name: ${formData.name},
Designation: ${formData.designation},
Email: ${formData.email},
Contact: ${formData.contactNumber || "N/A"},`;
};

/* Transcribed verbatim, branch for branch: a LISTING orders by the WorldBox win; a WARNING orders by wb_win when it has one, and otherwise by the company's... */
export const worldboxPostData = ({ company, id, userID, formData }) => {
  const status = profileStatus(company);
  const customerreference = customerReference(formData);

  if (status === LISTING) {
    return {
      win_number: company?.company?.win,
      companyID: id,
      userID,
      customerreference,
    };
  }

  if (status !== WARNING) return null;

  const win_no = company?.wb_win;
  if (win_no != null) {
    return { win_number: win_no, companyID: id, userID, customerreference };
  }

  const address = company?.companyAddress?.[0]?.company_address_detailsID;
  if (!(company?.companyAddress?.length > 0)) {
    return {
      companyID: id,
      companyname: company?.registered_company_name,
      address1: company?.country_code,
      city: company?.country_code,
      countrycode: company?.country_code,
      currency: "USD",
      userID,
      customerreference,
    };
  }

  return {
    companyID: id,
    companyname: company?.registered_company_name,
    address1: `${address?.address_line_1} ${address?.address_line_2} ${address?.area} ${address?.city} ${address?.state}`,
    city: address?.state,
    countrycode: address?.country,
    currency: "USD",
    userID,
    customerreference,
  };
};

export const useGetTheBusiness = ({ company, trevexId }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userInfo, token } = useSelector((state) => state.auth);

  const [creditNoticePopup, setCreditNoticePopup] = useState(false);
  /* Transcribed: `isGetTheCompanyOpen` in [id].jsx. The SECOND step of the
     offer for a listing or a draft — the acknowledgement form. */
  const [getCompanyPopup, setGetCompanyPopup] = useState(false);
  const [isContinue, setIsContinue] = useState(false);
  /* Transcribed shape, field for field. */
  const [formData, setFormData] = useState({
    name: "",
    designation: "",
    email: "",
    contactNumber: "",
  });

  const individualID = userInfo?.individual?.[0]?.individualID;
  const userID = userInfo?.individual?.[0]?.userID;
  const { data: individualData } = useGetIndividualQuery(
    { individualID, token },
    { skip: !individualID }
  );

  const userEnterprise = useMemo(() => eligibleEnterprises(userInfo), [userInfo]);
  const subscriptionActive = useMemo(
    () => activeSubscription(individualData),
    [individualData]
  );

  /* Transcribed: the live screen decides "verified" by whether the
     verifiedCompany object has anything in it, not by its presence. */
  const verifiedCompany = useMemo(() => {
    const v = company?.verifiedCompany;
    if (v == null) return false;
    return Object.keys(v).length !== 0;
  }, [company]);

  /* Transcribed: the context every branch parks before leaving. */
  const parkContext = () => {
    localStorage.removeItem("UpgradePlan");
    localStorage.setItem("payasyougoCompanyID", JSON.stringify(id));
    localStorage.setItem(
      "payasyougoCompany",
      JSON.stringify({
        name: company?.registered_company_name || company?.company?.name || null,
        country_code: company?.country_code || company?.company?.countrycode || null,
      })
    );
  };

  const toPlans = () => {
    scrollToTop();
    navigate("/plans", { state: { company_id: id } });
  };

  /* Transcribed from handleGetCompany. */
  const handleGetCompany = () => {
    parkContext();
    localStorage.setItem("paycompanyStatus", profileStatus(company));

    if (canSpendCredit({ subscriptionActive })) {
      setCreditNoticePopup(true);
      return;
    }
    if (!verifiedCompany) {
      toPlans();
      return;
    }
    scrollToTop();
    parkContext();
    navigate("/plans", { state: { company_id: id } });
  };

  /* Transcribed from createWorldBoxOrder (Tabs.jsx:498-602). */
  const createWorldBoxOrder = () => {
    localStorage.removeItem("UpgradePlan");
    const status = profileStatus(company);
    const postData = worldboxPostData({ company, id, userID, formData });

    /* setIsContinue(true) — BannerCompanyCard.jsx:918 and :926, in BOTH branches, and dropping it is what broke the second step. */
    if (status === LISTING || status === WARNING) {
      setIsContinue(true);
      localStorage.setItem("createWorldbox", true);
      localStorage.setItem("worldboxData", JSON.stringify(postData));
      parkContext();
      setGetCompanyPopup(true);
      return;
    }

    setIsContinue(true);
    parkContext();
    scrollToTop();
    toPlans();
  };

  /* Transcribed from [id].jsx's secondayListedHandler — the same parking, then
     the same split on whether a credit is available. */
  const secondayListedHandler = () => {
    const status = profileStatus(company);
    if (status === LISTING || status === WARNING) {
      const postData = worldboxPostData({ company, id, userID, formData });
      localStorage.setItem("createWorldbox", true);
      localStorage.setItem("worldboxData", JSON.stringify(postData));
    }
    parkContext();
    /* The live handler calls handleGetCompanyPopup() here ([id].jsx:284), which is a TOGGLE — the second step is open at this point, so the call closes it. */
    setGetCompanyPopup(false);
    setIsContinue(false);

    scrollToTop();
    if (canSpendCredit({ subscriptionActive })) {
      if (token) {
        setCreditNoticePopup(true);
        return;
      }
      navigate("/login?guest=true", { next: `/search/${id}` });
      return;
    }
    if (token) {
      toPlans();
      return;
    }
    navigate("/login?guest=true", { next: "/plans", state: { company_id: id } });
  };

  return {
    id,
    formData,
    setFormData,
    isContinue,
    setIsContinue,
    creditNoticePopup,
    setCreditNoticePopup,
    getCompanyPopup,
    setGetCompanyPopup,
    subscriptionActive,
    userEnterprise,
    canSpendCredit: canSpendCredit({ subscriptionActive }),
    handleGetCompany,
    createWorldBoxOrder,
    secondayListedHandler,
    handlePurchased: () => setCreditNoticePopup(true),
  };
};

export default useGetTheBusiness;
